/**
 * Convert an image file to a compressed base64 data URL.
 * No external storage needed — the string is saved directly in Firestore.
 *
 * @param {File} file - The image file to process
 * @param {number} maxWidth - Maximum width in pixels (default 800)
 * @param {number} quality - JPEG quality 0-1 (default 0.6)
 * @returns {Promise<string>} - base64 data URL string
 */
export const uploadStepImage = async (file, recipeId, partIndex, stepIndex, maxWidth = 800, quality = 0.85) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('无法读取图片文件'));
        reader.onload = (e) => {
            const img = new Image();
            img.onerror = () => reject(new Error('无法加载图片'));
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let { width, height } = img;

                if (width > maxWidth) {
                    height = (height / width) * maxWidth;
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                const dataUrl = canvas.toBlob
                    ? canvas.toDataURL('image/jpeg', quality)
                    : canvas.toDataURL('image/jpeg');

                resolve(dataUrl);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
};

/**
 * Delete a step image (no-op for base64 since it's stored inline).
 * @param {string} imageUrl - The base64 data URL (ignored)
 */
export const deleteStepImage = async (imageUrl) => {
    // No-op: base64 images are stored inline in Firestore,
    // they get removed when the step data is updated.
};

/**
 * Convert an image file to a square cropped, compressed base64 data URL.
 * Crops from center, outputs a fixed-size square.
 *
 * @param {File} file - The image file to process
 * @param {number} size - Output square size in pixels (default 400)
 * @param {number} quality - JPEG quality 0-1 (default 0.85)
 * @returns {Promise<string>} - base64 data URL string
 */
export const uploadCoverImage = async (file, size = 400, quality = 0.85) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('无法读取图片文件'));
        reader.onload = (e) => {
            const img = new Image();
            img.onerror = () => reject(new Error('无法加载图片'));
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');

                // Center crop to square
                const minDim = Math.min(img.width, img.height);
                const sx = (img.width - minDim) / 2;
                const sy = (img.height - minDim) / 2;

                ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);

                resolve(canvas.toDataURL('image/jpeg', quality));
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
};
